// src/services/push/firebase.js
import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";

const inBrowser =
  typeof window !== "undefined" && typeof navigator !== "undefined";
let app;
let messaging;
let listenersBound = false;
let permissionSyncBound = false;
let permissionSyncInFlight = null;

function log(...a) {
  console.log("[FCM]", ...a);
}

/** Push silencioso de posições do mapa de roteiros: a tela aberta reconsulta a API. */
export const TIPO_PUSH_LOCALIZACAO_ROTEIRO = "LOCALIZACAO_ROTEIRO";
export const EVENTO_LOCALIZACAO_ROTEIROS = "cajupe:localizacao-roteiros";

function avisarLocalizacaoRoteiros(data) {
  window.dispatchEvent(
    new CustomEvent(EVENTO_LOCALIZACAO_ROTEIROS, { detail: data || {} })
  );
}

function isLegacyIE() {
  if (!inBrowser || typeof document === "undefined") return false;
  return Boolean(document.documentMode);
}

function isSecurePushContext() {
  if (!inBrowser || typeof window === "undefined") return false;
  return (
    window.isSecureContext ||
    ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)
  );
}

function hasPushPrerequisites() {
  if (!inBrowser || isLegacyIE()) return false;
  const hasNotification = "Notification" in window;
  const hasServiceWorker = "serviceWorker" in navigator;
  const hasPushManager = "PushManager" in window;
  return (
    hasNotification &&
    hasServiceWorker &&
    hasPushManager &&
    isSecurePushContext()
  );
}

function normalizePermission(value) {
  if (value === "granted" || value === "denied" || value === "default") {
    return value;
  }
  return "default";
}

export function getPushPermissionState({
  fcmSupported = null,
  permission = null,
} = {}) {
  if (!hasPushPrerequisites()) return "unsupported";
  if (fcmSupported === false) return "unsupported";
  const currentPermission =
    permission ||
    (typeof Notification !== "undefined" ? Notification.permission : "default");
  return normalizePermission(currentPermission);
}

export async function isFcmSupported() {
  if (!hasPushPrerequisites()) return false;
  return isSupported().catch(() => false);
}

function ensureFirebase() {
  if (!inBrowser) return false;

  const cfg = {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
    authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.VUE_APP_FIREBASE_APP_ID,
  };

  if (!cfg.apiKey || !cfg.messagingSenderId || !cfg.appId) {
    log("Config Firebase ausente/incompleta");
    return false;
  }

  if (!app) app = initializeApp(cfg);
  if (!messaging) messaging = getMessaging(app);
  return true;
}

async function waitForState(sw) {
  return new Promise((resolve) => {
    if (!sw) return resolve();
    if (sw.state === "activated") return resolve();
    sw.addEventListener("statechange", () => {
      if (sw.state === "activated") resolve();
    });
  });
}
async function waitForActive(reg) {
  if (reg.active) return reg;
  await Promise.race([
    waitForState(reg.installing),
    waitForState(reg.waiting),
    navigator.serviceWorker.ready,
  ]);
  return reg;
}

async function requestNotificationPermissionCompat() {
  if (!inBrowser || !("Notification" in window)) return "default";

  try {
    const request = Notification.requestPermission.bind(Notification);

    // Safari/Firefox antigos usam callback; Chrome/Edge/Firefox atuais retornam Promise.
    if (request.length > 0) {
      return await new Promise((resolve) => {
        request((result) => {
          resolve(normalizePermission(result || Notification.permission));
        });
      });
    }

    const result = request();
    if (result && typeof result.then === "function") {
      return normalizePermission(await result);
    }
    return normalizePermission(result || Notification.permission);
  } catch (e) {
    log("requestPermission error:", e);
    return normalizePermission(Notification.permission);
  }
}

async function ensureNotificationPermission({ requestPermission = true } = {}) {
  if (!inBrowser || !("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  if (!requestPermission) return false;
  const result = await requestNotificationPermissionCompat();
  return result === "granted";
}
async function registerFcmServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.register(
    "/firebase-messaging-sw.js"
  );
  await waitForActive(reg);
  return reg;
}

/** Obtém/renova o token do navegador (Web Push) */
export async function getBrowserFcmToken({ requestPermission = false } = {}) {
  try {
    const supported = await isFcmSupported();
    log("isSupported =", supported);
    if (!supported) return null;

    const ok = ensureFirebase();
    if (!ok) return null;

    const hasPerm = await ensureNotificationPermission({ requestPermission });
    if ("Notification" in window) {
      log("Notification.permission =", Notification.permission);
    }
    if (!hasPerm) return null;

    // força update do SW no dev
    try {
      const res = await fetch("/firebase-messaging-sw.js", {
        cache: "no-store",
      });
      log("SW fetch status:", res.status);
    } catch (e) {
      log("SW fetch falhou:", e);
    }

    const swReg = await registerFcmServiceWorker();
    if (!swReg) return null;

    const vapidKey = (process.env.VUE_APP_FIREBASE_VAPID_KEY || "").trim();
    const looksValidVapid = /^B[A-Za-z0-9_-]{80,}$/.test(vapidKey);
    if (!looksValidVapid) {
      log("VAPID PUBLIC KEY malformada/ausente");
      return null;
    }

    log("chamando getToken...");
    let token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swReg,
    }).catch((e) => {
      log("getToken error (1):", e);
      return null;
    });

    if (!token) {
      await navigator.serviceWorker.ready.catch((e) => {
        log("serviceWorker.ready falhou:", e);
      });
      log("retry getToken após ready()...");
      token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swReg,
      }).catch((e) => {
        log("getToken error (2):", e);
        return null;
      });
    }

    log("token obtido:", token ? token.slice(0, 12) + "..." : null);
    return token || null;
  } catch (e) {
    log("getBrowserFcmToken error:", e);
    return null;
  }
}

function silentSyncPushToken(store) {
  if (!store || typeof store.dispatch !== "function") return Promise.resolve();
  if (permissionSyncInFlight) return permissionSyncInFlight;

  permissionSyncInFlight = store
    .dispatch("ENSURE_PUSH_TOKEN_SYNC", { requestPermission: false })
    .catch((e) => {
      log("auto-sync token error:", e);
    })
    .finally(() => {
      permissionSyncInFlight = null;
    });

  return permissionSyncInFlight;
}

export async function initPushPermissionAutoSync(store) {
  if (!inBrowser || permissionSyncBound) return;
  if (!store || typeof store.dispatch !== "function") return;

  const permissionState = getPushPermissionState();
  if (permissionState === "unsupported") {
    log("auto-sync desabilitado: push não suportado neste navegador/ambiente.");
    return;
  }

  permissionSyncBound = true;
  let lastPermission = normalizePermission(Notification.permission);

  const checkPermissionAndSync = async () => {
    const currentPermission = normalizePermission(Notification.permission);
    if (currentPermission !== lastPermission) {
      log(
        "Notification.permission mudou:",
        `${lastPermission} -> ${currentPermission}`
      );
      lastPermission = currentPermission;
    }

    if (currentPermission === "granted") {
      await silentSyncPushToken(store);
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      checkPermissionAndSync();
    }
  };

  window.addEventListener("focus", checkPermissionAndSync);
  window.addEventListener("pageshow", checkPermissionAndSync);
  document.addEventListener("visibilitychange", onVisibilityChange);

  if (
    navigator.permissions &&
    typeof navigator.permissions.query === "function"
  ) {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "notifications",
      });
      if (
        permissionStatus &&
        typeof permissionStatus.addEventListener === "function"
      ) {
        permissionStatus.addEventListener("change", checkPermissionAndSync);
      } else if (permissionStatus) {
        permissionStatus.onchange = checkPermissionAndSync;
      }
    } catch (e) {
      log("Permissions API indisponível para notifications:", e?.message || e);
    }
  }

  await checkPermissionAndSync();
}

function normalizeFnName(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^A-Za-z0-9]+/g, "")
    .toUpperCase();
}

function safeJsonParse(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch (err) {
    console.warn("[FCM] JSON parse falhou", err, value);
    return null;
  }
}

function buildDispatchPayload({ functionName, data, message }) {
  const detail = {
    functionName,
    rawData: data,
    message,
  };

  const maybePayload = data?.payload ?? message?.data?.payload;
  const parsedPayload = safeJsonParse(maybePayload);
  if (parsedPayload !== null) {
    detail.payload = parsedPayload;
  } else if (typeof maybePayload !== "undefined") {
    detail.payload = maybePayload;
  }

  return detail;
}

/** Liga listeners para foreground (onMessage) e mensagens do SW (background/click) */
export async function initMessagingListeners(store) {
  if (!inBrowser || listenersBound) return;
  const supported = await isSupported().catch(() => false);
  log("isSupported =", supported);
  if (!supported || listenersBound) return;
  const ok = ensureFirebase();
  if (!ok) return;

  // Foreground
  try {
    onMessage(messaging, async (payload) => {
      log("onMessage foreground:", payload);
      const data = payload?.data || {};
      const type = String(data?.type || "");
      const fn = normalizeFnName(data?.functionName);
      log("foreground data:", data, "type:", type, "fn:", fn);
      if (type === TIPO_PUSH_LOCALIZACAO_ROTEIRO) {
        avisarLocalizacaoRoteiros(data);
        return;
      }
      if (type === "function" && fn) {
        const detail = buildDispatchPayload({
          functionName: fn,
          data,
          message: payload,
        });
        await store.dispatch("HANDLE_PUSH_FUNCTION", detail);
      }
    });
  } catch (e) {
    console.warn("[FCM] onMessage attach error:", e);
  }

  // Mensagens vindas do SW (bg e clique)
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", async (event) => {
      log("SW -> page message:", event?.data);
      const msg = event?.data || {};

      if (msg?.type === "FCM_BG_MESSAGE") {
        const data = msg?.payload?.data || {};
        const fn = normalizeFnName(data?.functionName);
        log("FCM_BG_MESSAGE data:", data, "fn:", fn);
        if (String(data?.type) === TIPO_PUSH_LOCALIZACAO_ROTEIRO) {
          avisarLocalizacaoRoteiros(data);
        }
        if (String(data?.type) === "function" && fn) {
          const detail = buildDispatchPayload({
            functionName: fn,
            data,
            message: msg?.payload,
          });
          await store.dispatch("HANDLE_PUSH_FUNCTION", detail);
        }
      }

      if (msg?.type === "NOTIFICATION_CLICK") {
        const data = msg?.data || {};
        const fn = normalizeFnName(data?.functionName);
        log("NOTIFICATION_CLICK data:", data, "fn:", fn);
        if (String(data?.type) === "function" && fn) {
          const detail = buildDispatchPayload({
            functionName: fn,
            data,
            message: msg,
          });
          await store.dispatch("HANDLE_PUSH_FUNCTION", detail);
        }
      }
    });
  }

  listenersBound = true;
  log("listeners bound");
}
